import {useState} from 'react';
import { ResumeSkills } from '../../types';
import { Plus, X } from 'lucide-react';

interface SkillsSectionProps {
    skills: ResumeSkills;
    onChange: (skills: ResumeSkills) => void;
}

export function SkillsSection({ skills, onChange }: SkillsSectionProps) {
    const [newSkillInputs, setNewSkillInputs] = useState<{ [key: number]: string }>({});

    const categories = skills?.categories || [];

    const addCategory = () => {
        onChange({
            categories: [...categories, { name: '', items: [] }],
        });
    };

    const removeCategory = (index: number) => {
        onChange({
            categories: categories.filter((_, i) => i !== index),
        });
    };

    const updateCategoryName = (index: number, name: string) => {
        const newCategories = [...categories];
        newCategories[index] = { ...newCategories[index], name };
        onChange({ categories: newCategories });
    };

    const addSkill = (categoryIndex: number) => {
        const skillText = newSkillInputs[categoryIndex]?.trim();
        if (!skillText) return;

        const newCategories = [...categories];
        newCategories[categoryIndex].items.push(skillText);
        onChange({ categories: newCategories });

        setNewSkillInputs({ ...newSkillInputs, [categoryIndex]: '' });
    };

    const removeSkill = (categoryIndex: number, skillIndex: number) => {
        const newCategories = [...categories];
        newCategories[categoryIndex].items = newCategories[categoryIndex].items.filter(
            (_, i) => i !== skillIndex
        );
        onChange({ categories: newCategories });
    };

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <h2 className="section-title mb-0">Skills</h2>
                <button onClick={addCategory} className="btn btn-secondary text-sm flex items-center gap-1">
                    <Plus size={16} />
                    Add Category
                </button>
            </div>

            <div className="space-y-4">
        {categories.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4 relative">
                        <button
                            onClick={() => removeCategory(categoryIndex)}
                            className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                        >
                            <X size={20} />
                        </button>

                        <div className="space-y-3">
                            <div>
                                <label className="label">Category Name</label>
                                <input
                                    type="text"
                                    value={category.name}
                                    onChange={(e) => updateCategoryName(categoryIndex, e.target.value)}
                                    className="input"
                                    placeholder="e.g., Programming Languages, Tools, Frameworks"
                                />
                            </div>

                            <div>
                                <label className="label">Skills</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={newSkillInputs[categoryIndex] || ''}
                                        onChange={(e) =>
                                            setNewSkillInputs({ ...newSkillInputs, [categoryIndex]: e.target.value })
                                        }
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addSkill(categoryIndex);
                                            }
                                        }}
                                        className="input flex-1"
                                        placeholder="Type a skill and press Enter"
                                    />
                                    <button
                                        onClick={() => addSkill(categoryIndex)}
                                        className="btn btn-secondary"
                                    >
                                        Add
                                    </button>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {(category.items || []).map((skill, skillIndex) => (
                                        <span
                                            key={skillIndex}
                                            className="inline-flex items-center gap-1 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm"
                                        >
                                            {skill}
                                            <button
                                                onClick={() => removeSkill(categoryIndex, skillIndex)}
                                                className="hover:text-primary-900"
                                            >
                                                <X size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {skills.categories.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No skill categories added yet</p>
                )}
            </div>
        </div>
    );
}
